import io from "socket.io-client";
import api from "../services/api";
import { getSocketPath, getSocketUrl } from "../config";

// Token de sessão SEMPRE lido na hora de conectar: o localStorage é a fonte de verdade
// (o refresh grava lá) e o header da api pode ainda não existir na carga da página.
const currentToken = () => {
  try {
    const stored = localStorage.getItem("token");
    if (stored) return `Bearer ${JSON.parse(stored)}`;
  } catch (e) {
    // ignora token corrompido e cai no header
  }
  return api.defaults.headers.Authorization || "";
};

class SocketWorker {
  constructor(companyId, userId) {
    const inst = SocketWorker.instance;
    // Chamada sem identidade (ex.: tela que só quer o socket já aberto): nunca derruba o atual
    if (inst && (!companyId || !userId)) return inst;
    if (inst) {
      // Mesmo usuário/empresa: reaproveita. Se o socket morreu (ex.: criado sem token na
      // carga da página) ou trocou de usuário/empresa, reconstrói com o token atual.
      const sameIdentity = inst.companyId === companyId && inst.userId === userId;
      const alive = !!inst.socket && (inst.socket.connected || inst.socket.active);
      if (sameIdentity && alive && inst.token === currentToken()) return inst;
      inst.teardown();
    }
    this.companyId = companyId;
    this.userId = userId;
    this.token = currentToken();
    this.socket = null;
    this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
    this.configureSocket();
    SocketWorker.instance = this;
    return this;
  }

  configureSocket() {
    this.token = currentToken();
    this.socket = io(getSocketUrl(this?.companyId), {
      path: getSocketPath(),
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId, token: this.token }
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO", reason);
      this.reconnectAfterDelay();
    });

    // Reaplica os listeners registrados antes de uma reconstrução
    Object.entries(this.eventListeners).forEach(([event, cbs]) =>
      cbs.forEach(cb => this.socket.on(event, cb))
    );
  }

  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      if (callback) {
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
    }
  }

  // Derruba o socket sem perder a lista de listeners (usado na reconstrução)
  teardown() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    if (SocketWorker.instance === this) SocketWorker.instance = null;
  }

  disconnect() {
    this.eventListeners = {};
    this.teardown();
    console.log("Socket desconectado manualmente");
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket) return this.connect();
      if (this.socket.connected) return;
      // Reconecta com o token vigente: se o servidor derrubou por token vazio/expirado,
      // a próxima tentativa já sobe com o renovado (o refresh grava no localStorage).
      const token = currentToken();
      if (token !== this.token) {
        this.token = token;
        this.socket.io.opts.query = { ...this.socket.io.opts.query, token };
      }
      console.log("Tentando reconectar após desconexão");
      this.socket.connect();
    }, 1000);
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  forceReconnect() {
    this.reconnectAfterDelay();
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
